import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../../store';
import {
    nasAPI, vlanProfileAPI, planAPI, authSetAPI,
    userTypeAPI, templateAPI, voucherAPI, deviceAPI,
    pmsAPI, branchAPI,
} from '../../services/api';
import './SetupGuide.css';

const GROUPS = [
    {
        label: 'Infrastructure',
        steps: [
            {
                key: 'nas',
                icon: 'fa-server',
                name: 'NAS Devices',
                desc: 'Register routers / hotspot gateways for RADIUS authentication',
                path: '/nas',
                roles: ['superadmin', 'tenant_admin', 'branch_admin'],
                fetch: () => nasAPI.getAll(),
                count: (r) => r.data?.data?.length ?? r.data?.total ?? 0,
            },
            {
                key: 'vlan',
                icon: 'fa-network-wired',
                name: 'VLAN Profiles',
                desc: 'Map VLAN IDs to plans with independent bandwidth and quota',
                path: '/vlan-profiles',
                roles: ['superadmin', 'tenant_admin', 'branch_admin'],
                fetch: () => vlanProfileAPI.getAll(),
                count: (r) => r.data?.data?.length ?? r.data?.total ?? 0,
            },
            {
                key: 'branches',
                icon: 'fa-code-branch',
                name: 'Branches',
                desc: 'Define property locations / branch sites that use the hotspot',
                path: '/branches',
                roles: ['superadmin', 'tenant_admin', 'branch_admin'],
                fetch: () => branchAPI.getAll(),
                count: (r) => r.data?.data?.length ?? r.data?.total ?? 0,
            },
        ],
    },
    {
        label: 'Services & Policies',
        steps: [
            {
                key: 'plans',
                icon: 'fa-tags',
                name: 'Internet Plans',
                desc: 'Create bandwidth packages (speed limits, data quota, validity)',
                path: '/plans',
                roles: ['superadmin', 'tenant_admin', 'branch_admin'],
                fetch: () => planAPI.getAll(),
                count: (r) => r.data?.data?.length ?? r.data?.total ?? 0,
            },
            {
                key: 'auth-sets',
                icon: 'fa-shield-alt',
                name: 'Authorization Sets',
                desc: 'Define what users receive after authentication — bandwidth, VLAN, schedule',
                path: '/auth-sets',
                roles: ['superadmin', 'tenant_admin'],
                fetch: () => authSetAPI.getAll(),
                count: (r) => r.data?.data?.length ?? r.data?.total ?? 0,
            },
            {
                key: 'user-types',
                icon: 'fa-users-cog',
                name: 'User Types',
                desc: 'Guest categories (Staff, VIP, Corporate) with automatic plan assignment',
                path: '/user-types',
                roles: ['superadmin', 'tenant_admin', 'branch_admin'],
                fetch: () => userTypeAPI.getAll(),
                count: (r) => r.data?.data?.length ?? r.data?.total ?? 0,
            },
        ],
    },
    {
        label: 'Portal & Access',
        steps: [
            {
                key: 'templates',
                icon: 'fa-paint-brush',
                name: 'Splash Pages',
                desc: 'Design the WiFi captive portal login page shown to guests',
                path: '/templates',
                roles: ['superadmin', 'tenant_admin', 'branch_admin'],
                fetch: () => templateAPI.getAll(),
                count: (r) => r.data?.data?.length ?? r.data?.total ?? 0,
            },
            {
                key: 'vouchers',
                icon: 'fa-ticket-alt',
                name: 'Vouchers',
                desc: 'Generate single-use access codes for guest WiFi distribution',
                path: '/vouchers',
                roles: ['superadmin', 'tenant_admin', 'branch_admin', 'support'],
                fetch: () => voucherAPI.getAll(),
                count: (r) => r.data?.data?.total ?? r.data?.data?.length ?? r.data?.total ?? 0,
            },
        ],
    },
    {
        label: 'Device Management',
        steps: [
            {
                key: 'devices',
                icon: 'fa-laptop',
                name: 'Connected Devices',
                desc: 'Static MAC whitelist / blacklist for TVs, cameras, and shared devices',
                path: '/devices',
                roles: ['superadmin', 'tenant_admin', 'branch_admin', 'support'],
                fetch: () => deviceAPI.getAll(),
                count: (r) => r.data?.data?.total ?? r.data?.data?.length ?? r.data?.total ?? 0,
            },
        ],
    },
    {
        label: 'Integrations',
        steps: [
            {
                key: 'pms',
                icon: 'fa-plug',
                name: 'PMS Integration',
                desc: 'Connect hotel Property Management System for automatic guest WiFi',
                path: '/integrations',
                roles: ['superadmin', 'tenant_admin'],
                fetch: () => pmsAPI.getConfigs(),
                count: (r) => {
                    const d = r.data?.data;
                    if (Array.isArray(d)) return d.filter(c => c.enabled).length;
                    return d?.enabled ? 1 : 0;
                },
            },
        ],
    },
];

export default function SetupGuide() {
    const { admin } = useAuthStore();
    const role = admin?.role || 'viewer';

    // counts keyed by step key: null = loading, number = result
    const [counts, setCounts] = useState({});

    // flatten all steps this role can see
    const visibleGroups = GROUPS.map(g => ({
        ...g,
        steps: g.steps.filter(s => s.roles.includes(role)),
    })).filter(g => g.steps.length > 0);

    const allSteps = visibleGroups.flatMap(g => g.steps);

    useEffect(() => {
        // fire all fetches in parallel
        allSteps.forEach(step => {
            setCounts(prev => ({ ...prev, [step.key]: null })); // mark loading
            step.fetch()
                .then(res => {
                    const n = step.count(res);
                    setCounts(prev => ({ ...prev, [step.key]: n }));
                })
                .catch(() => {
                    setCounts(prev => ({ ...prev, [step.key]: 0 }));
                });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const doneCount = allSteps.filter(s => (counts[s.key] ?? 0) > 0).length;
    const pct = allSteps.length ? Math.round((doneCount / allSteps.length) * 100) : 0;

    return (
        <div className="sg-page">
            {/* Header */}
            <div className="sg-header">
                <h1 className="sg-title">
                    <i className="fas fa-rocket"></i>
                    Setup Guide
                </h1>
                <p className="sg-subtitle">
                    Complete these steps to fully configure your hotspot property.
                </p>
                <div className="sg-progress-bar-wrap">
                    <div className="sg-progress-bar-fill" style={{ width: `${pct}%` }} />
                </div>
                <p className="sg-progress-label">
                    <strong>{doneCount}</strong> of {allSteps.length} steps configured
                    {pct === 100 && (
                        <span style={{ marginLeft: 8, color: '#16a34a' }}>
                            <i className="fas fa-check-circle" style={{ marginRight: 4 }} />
                            All done!
                        </span>
                    )}
                </p>
            </div>

            {/* Groups */}
            {visibleGroups.map(group => (
                <div className="sg-section" key={group.label}>
                    <p className="sg-section-label">{group.label}</p>
                    <div className="sg-steps">
                        {group.steps.map(step => {
                            const raw = counts[step.key];
                            const loading = raw === null || raw === undefined;
                            const n = raw ?? 0;
                            const done = n > 0;

                            return (
                                <div key={step.key} className={`sg-step-card ${done ? 'done' : ''}`}>
                                    {/* Icon */}
                                    <div className={`sg-step-num ${done ? 'done-num' : 'pending-num'}`}>
                                        <i className={`fas ${done ? 'fa-check' : step.icon}`} />
                                    </div>

                                    {/* Body */}
                                    <div className="sg-step-body">
                                        <div className="sg-step-top">
                                            <span className="sg-step-name">{step.name}</span>
                                            {!loading && (
                                                <span className={`sg-count-chip ${done ? 'done-chip' : 'pending-chip'}`}>
                                                    {done ? `${n} configured` : 'Not set up'}
                                                </span>
                                            )}
                                            {loading && (
                                                <span className="sg-count-chip pending-chip" style={{ width: 72 }}>
                                                    <span className="sg-skeleton" style={{ width: '100%', display: 'block' }} />
                                                </span>
                                            )}
                                        </div>
                                        <div className="sg-step-desc">{step.desc}</div>
                                    </div>

                                    {/* Right */}
                                    <div className="sg-step-right">
                                        <span className={`sg-status-badge ${done ? 'done-badge' : 'pending-badge'}`}>
                                            {done ? 'Done' : 'Pending'}
                                        </span>
                                        <Link
                                            to={step.path}
                                            className={`sg-configure-btn ${done ? 'done-btn' : 'pending-btn'}`}
                                        >
                                            <i className={`fas ${done ? 'fa-pencil-alt' : 'fa-arrow-right'}`} />
                                            {done ? 'Manage' : 'Set Up'}
                                        </Link>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}
        </div>
    );
}
